import { useEffect, useRef, useSyncExternalStore } from 'react';
import { api } from './api';
import { config } from './config';

/** Zendesk locates its own tag by this id, so it is not decorative. */
const SNIPPET_ID = 'ze-snippet';

/** Resolves once the snippet has loaded and its handlers are subscribed. */
let loading: Promise<boolean> | null = null;

/**
 * The widget, read fresh on every call. Never hold on to what this returns.
 *
 * `snippet.js` defines `window.zE` as a queueing stub and replaces it with the
 * real implementation once the widget boots. Anything registered against the
 * stub *before* boot is replayed — which is why the initial `hide` and the
 * `messenger:on` subscriptions below take effect — but a command sent to that
 * same stub *after* boot lands in a queue nothing drains again and is silently
 * dropped. It fails without an error, which is what made it invisible.
 *
 * Measured on the live widget: with a cached handle, the header button's `show`
 * never arrived, while the identical call on `window.zE` opened the panel
 * immediately. react-use-zendesk re-reads `window.zE` per call for this reason.
 */
function zendesk(): ZendeskWidget | null {
  return window.zE ?? null;
}

export interface SupportChatState {
  /** The widget is loaded and reachable. False = render no Support control. */
  readonly available: boolean;
  /** The messenger panel is on screen. */
  readonly open: boolean;
}

/**
 * Zendesk owns the real open/closed state — the panel has its own close button,
 * and on mobile the OS back gesture dismisses it — so this mirrors the widget's
 * events rather than tracking what we last told it to do. Kept in a module store
 * so every Support control reads one source of truth instead of each guessing.
 */
let state: SupportChatState = { available: false, open: false };
const listeners = new Set<() => void>();

function publish(next: Partial<SupportChatState>): void {
  const merged = { ...state, ...next };
  if (merged.available === state.available && merged.open === state.open) return;
  state = merged;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const HIDE_RETRY_MS = 200;
/** ~10s. Long enough for a cold widget boot on a slow connection. */
const HIDE_MAX_ATTEMPTS = 50;

let hideTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Whether the panel on screen is one this app asked for.
 *
 * The launcher is hidden, so the only legitimate way in is the Support button —
 * which makes an unrequested `open` a restored session rather than a customer
 * action. See the open handler in `attach`.
 */
let requestedOpen = false;

/**
 * Whether Zendesk has acted on a `hide` yet.
 *
 * `iframe#launcher` is the widget's own id and the only way to observe that the
 * command landed — the same anchor react-use-zendesk reaches for. If Zendesk
 * ever renames it this reads "not hidden", the retries below run out, and the
 * launcher stays visible: cosmetic, not a broken portal.
 */
function launcherIsHidden(): boolean {
  const launcher = document.querySelector('iframe#launcher');
  return launcher !== null && getComputedStyle(launcher).visibility === 'hidden';
}

function stopHiding(): void {
  if (hideTimer === null) return;
  clearInterval(hideTimer);
  hideTimer = null;
}

/**
 * Hide the launcher, and keep asking until Zendesk listens.
 *
 * A single `hide` on the snippet's load event is silently dropped — the widget
 * has not booted yet, and when it does it renders the launcher regardless. That
 * is not a race that can be won by waiting for a ready event, because the
 * messaging widget does not fire one, so this re-issues on an interval and stops
 * as soon as the command demonstrably lands. Measured against the live widget it
 * settles about a second in, before the launcher has ever painted, so nothing
 * flashes in the corner.
 */
function hideLauncher(): void {
  const zE = zendesk();
  if (!zE) return;
  zE('messenger', 'hide');
  if (hideTimer !== null) return;

  let attempts = 0;
  hideTimer = setInterval(() => {
    attempts += 1;
    zendesk()?.('messenger', 'hide');
    if (launcherIsHidden() || attempts >= HIDE_MAX_ATTEMPTS) stopHiding();
  }, HIDE_RETRY_MS);
}

/** Subscribe to the widget's own events. Returns false if it never loaded. */
function attach(): boolean {
  const zE = zendesk();
  if (!zE) return false;

  zE('messenger:on', 'open', () => {
    // Zendesk persists the panel's open/closed state across page loads and
    // restores it on boot, firing `open` for a panel nobody asked for. With the
    // launcher hidden that restored panel renders nothing, so taking the event
    // at face value leaves the button convinced chat is already on screen — and
    // the next press closes what was never open, which looks exactly like a
    // button that does nothing. Anything we did not ask for gets closed back
    // down, which also clears the stored state for the next load.
    if (!requestedOpen) {
      zendesk()?.('messenger', 'close');
      return;
    }
    publish({ open: true });
  });
  zE('messenger:on', 'close', () => {
    requestedOpen = false;
    publish({ open: false });
    // Dismissing from inside the panel has to put the launcher away again, or
    // the corner bubble the header button exists to replace comes back on first
    // use and never leaves.
    hideLauncher();
  });

  // The launcher is never wanted: the header button is the only way in, so it is
  // hidden from the moment the snippet lands rather than at some later "should
  // we show it" decision.
  hideLauncher();
  publish({ available: true });
  return true;
}

/**
 * Inject the Zendesk snippet, or don't.
 *
 * Injected from here rather than sitting in index.html for two reasons.
 * `config.supportChatEnabled` is a real off switch: with chat disabled the tag
 * is never created, the portal makes no request to Zendesk, and no launcher
 * appears. And loading can be deferred until there is a signed-in customer to
 * load it for — see useSupportWidget. A hardcoded tag could do neither.
 *
 * Resolves false on any failure — a blocked or failed CDN fetch must leave
 * callers with "no widget", never with a promise that never settles.
 */
function loadSupportWidget(): Promise<boolean> {
  if (loading) return loading;

  if (!config.supportChatEnabled) {
    loading = Promise.resolve(false);
    return loading;
  }

  loading = new Promise((resolve) => {
    const existing = document.getElementById(SNIPPET_ID);
    if (existing) {
      resolve(attach());
      return;
    }

    const script = document.createElement('script');
    script.id = SNIPPET_ID;
    // Async because this is no longer a parser-blocking tag in <head>. Support
    // chat is never worth delaying first paint for.
    script.async = true;
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(
      config.zendeskWidgetKey,
    )}`;
    script.addEventListener('load', () => resolve(attach()));
    script.addEventListener('error', () => resolve(false));
    document.head.appendChild(script);
  });

  return loading;
}

/** Show and open the messenger, or close it and put it away. */
export function toggleSupportChat(): void {
  const zE = zendesk();
  // `available` and not just the handle, so a control left on screen after a
  // sign-out cannot reopen a session that has been ended.
  if (!zE || !state.available) return;

  if (state.open) {
    // No `hide` here — the close handler does it, and that is the one path that
    // also covers the panel being dismissed from inside.
    requestedOpen = false;
    zE('messenger', 'close');
    // Optimistic, because a `close` event only arrives if the widget really was
    // open. Trusting the event alone would leave the button stuck reading "open"
    // for the rest of the session if our state had drifted; the worst this costs
    // is one extra click, which re-opens.
    publish({ open: false });
    return;
  }

  requestedOpen = true;
  // The boot-time retries are still running if this is an early click, and they
  // would hide the widget straight back out from under `show`.
  stopHiding();
  // `open` is ignored while the widget is hidden, so the order matters.
  zE('messenger', 'show');
  zE('messenger', 'open');
}

/** Subscribe a Support control to the widget's own open/closed state. */
export function useSupportChat(): SupportChatState {
  return useSyncExternalStore(subscribe, () => state);
}

/**
 * Zendesk tags are single tokens — spaces and punctuation are not allowed — so an
 * organization name has to be flattened before it can be one.
 */
function orgTag(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `x5-org-${slug || 'unknown'}`;
}

/**
 * Load the support widget for a signed-in customer, and tear it down on the way
 * out.
 *
 * Chat is offered to signed-in customers only, and this is where that holds:
 * while signed out the snippet is never injected at all, so a visitor to the
 * sign-in screen gets no widget, no launcher and no request to Zendesk — not a
 * hidden widget that a console call could bring back.
 *
 * Driven by auth state rather than called from the sign-out buttons: `signOut()`
 * is invoked from both the account menu and the /logout route, and reacting to
 * the transition means neither has to remember to tell Zendesk about it. Same
 * reasoning as splitting the app on `authStatus` in App.tsx.
 */
export function useSupportWidget(signedIn: boolean): void {
  // The widget keeps its own session in local storage, so a second `loginUser`
  // is not merely redundant — it restarts the handshake. StrictMode re-running
  // this effect on mount makes that a certainty in development.
  //
  // Reset on sign-out so the next customer to sign in on this browser is
  // identified afresh. Without a `logoutUser` the previous conversation stays in
  // local storage until `loginUser` replaces the identity, so on a shared
  // machine there is a window where it is still on disk.
  const identified = useRef(false);

  useEffect(() => {
    if (!signedIn) {
      // Null for a visitor who has never signed in — there is nothing to undo,
      // because nothing was ever loaded. Past a sign-out the script cannot be
      // unloaded, so the widget is put away instead: the panel closes, the
      // launcher hides, and `available: false` takes the Support control out of
      // the tree.
      //
      // Deliberately no `logoutUser`. It is the documented way to end a Zendesk
      // session, but it also tears down and re-initialises the messenger, and
      // the identity is re-established by `loginUser` on the next sign-in
      // anyway. See the note on `identified` for what this leaves behind.
      const zE = zendesk();
      if (!zE) return;
      requestedOpen = false;
      zE('messenger', 'close');
      hideLauncher();
      identified.current = false;
      publish({ available: false, open: false });
      return;
    }

    let cancelled = false;

    void loadSupportWidget().then((ready) => {
      // False when support chat is switched off, or when the snippet failed to
      // load. Either way the portal carries on without it.
      if (cancelled || !ready) return;

      // `attach` only runs on the first load, so signing back in re-arms this
      // rather than leaving the Support button missing for the second session.
      publish({ available: true });

      if (identified.current) return;

      // The first token is fetched *before* `loginUser`, not from inside its
      // callback.
      //
      // `loginUser` puts the messenger into a login flow that ends only when the
      // callback supplies a token, and there is no way to withdraw it: if the
      // fetch fails and the callback is never called, the widget sits in that
      // state and refuses to open. The token route answers 503 until a signing
      // key exists in the Zendesk Admin Center, so that is a state this reaches
      // in normal operation, not just a broken one. Getting a token in hand
      // first means the failure mode is anonymous-but-working chat instead.
      void api
        .supportToken()
        .then(({ token, organization }) => {
          // Claimed here rather than before the fetch. StrictMode tears this
          // effect down and re-runs it before the request settles, so a flag set
          // up front would have the second run skip the login that the first run
          // abandoned on `cancelled` — and nothing would ever be identified.
          if (cancelled || identified.current) return;
          identified.current = true;

          // Spent on the widget's first callback; every later one is a 401
          // refresh and has to fetch afresh, which is how a five-minute token
          // survives a longer conversation.
          let firstToken: string | null = token;

          zendesk()?.(
            'messenger',
            'loginUser',
            (setToken) => {
              if (firstToken !== null) {
                const pending = firstToken;
                firstToken = null;
                setToken(pending);
                return;
              }
              void api
                .supportToken()
                .then((refreshed) => setToken(refreshed.token))
                .catch(() => {
                  // Nothing useful to do: the conversation continues on the
                  // token it already has until that one expires.
                });
            },
            (error) => {
              if (error) {
                console.warn('Zendesk rejected the identity token', error.reason);
                return;
              }
              // Advisory context for triage. Set from the browser, so it is
              // suggestion rather than evidence — never gate anything on it.
              // Applied after login rather than before, because `loginUser`
              // re-initialises the messenger and would discard it.
              if (organization) {
                zendesk()?.('messenger:set', 'conversationTags', [orgTag(organization.name)]);
              }
              // That same re-initialisation can put the launcher back.
              hideLauncher();
            },
          );
        })
        .catch(() => {
          // Chat stays anonymous and, crucially, still opens. Nothing to reset:
          // `identified` is only claimed once a token is actually in hand.
        });
    });

    return () => {
      cancelled = true;
    };
  }, [signedIn]);
}
